import { css } from '@emotion/react'
import { memo, useCallback, useMemo } from 'react'
import { VList } from 'virtua'
import type { Participant } from '../../../../../types'
import type { RoomType } from '../../../../../types/mfc'
import { setFullWindow } from '../../../../actions/layout'
import { openMenuDialog } from '../../../../actions/menu'
import { openMessage } from '../../../../actions/message'
import { openQuickMenu } from '../../../../app-state/modals/state.confirmation'
import { closeDrawer } from '../../../../app-state/modals/state.drawer'
import { getNameStyles } from '../../../../utilities/colors'
import i18n from '../../../../utilities/i18n'
import { safeObj } from '../../../../utilities/safe'
import { TransitionTypes } from '../../../../utilities/transitions'
import IconButton from '../../../Buttons/IconButton'
import { useModelColor } from '../../../Hooks/useModelColor'
import { useRoomSelector } from '../../../Hooks/useRoom'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { useUserSelector } from '../../../Hooks/useUser'
import Message from '../../../Icons/Message'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'

const AVATAR_SIZE = 40
const AVATAR_SIZE_SIDEBAR = 26
const VERTICAL_PADDING = getThemeSpacing(0.5, true)
const VERTICAL_PADDING_SIDEBAR = 1.5
const ROW_HEIGHT = AVATAR_SIZE + 2 * VERTICAL_PADDING
const ROW_HEIGHT_SIDEBAR = AVATAR_SIZE_SIDEBAR + 2 * VERTICAL_PADDING_SIDEBAR

const rootStyles = css({
    width: '100%',
    height: '100%',
})
const subtitleStyles = css({
    fontSize: 11,
    color: palette.secondaryText,
})
const participantContainerStyles = css({
    width: `calc(100% - ${2 * getThemeSpacing(2, true)}px) !important`,
    marginLeft: getThemeSpacing(2),
    marginRight: getThemeSpacing(2),
    borderBottom: '1px solid ' + palette.drawerDivider,
    '&:last-of-type': {
        borderBottom: 'none',
    },
})
const participantContainerSidebarStyles = css({
    marginLeft: 0,
    marginRight: 0,
    borderBottom: 'none',
    '&:last-of-type': {
        borderBottom: 'none',
    },
})
const participantRowStyles = css({
    cursor: 'pointer',
})
const participantStyles = css({
    display: 'flex',
    position: 'relative' as const,
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: VERTICAL_PADDING,
    paddingBottom: VERTICAL_PADDING,
})
const participantSidebarStyles = css({
    display: 'flex',
    position: 'relative' as const,
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: VERTICAL_PADDING_SIDEBAR,
    paddingBottom: VERTICAL_PADDING_SIDEBAR,
})
const nameStyles = css({
    marginLeft: getThemeSpacing(2),
    fontSize: '1rem',
})
const nameSidebarStyles = css({
    marginLeft: getThemeSpacing(0.75),
    fontSize: '0.8125rem',
})
const buttonStyles = css({
    position: 'absolute' as const,
    right: getThemeSpacing(1),
})
const guestsStyles = css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center' as const,
    fontSize: '1rem',
    fontWeight: 700,
})
const guestsSidebarStyles = css({
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: getThemeSpacing(4),
})

interface GuestViewerCount {
    guestCount?: number
    viewerCount?: number
}

type ParticipantType = Partial<Participant> & GuestViewerCount

interface IParticipant {
    participant: ParticipantType
    isSidebar: boolean
    streamType: RoomType
}

const ParticipantRow = memo((props: IParticipant) => {
    const { participant, isSidebar, streamType } = props
    const { viewerCount, guestCount, id } = participant
    const slug = useUserSelector(id, 'slug')
    const name = useUserSelector(id, 'name')
    const level = useUserSelector(id, 'level')
    const color = useUserSelector(id, 'color')
    const isOffline = useUserSelector(id, 'isOffline')
    const isModel = useUserSelector(id, 'isModel')

    const avatarSize = isSidebar ? AVATAR_SIZE_SIDEBAR : AVATAR_SIZE
    const userChatColor = useModelColor(color)
    const nameStyle = getNameStyles(level, userChatColor)

    const onClick = useCallback(
        (e) => {
            if (isModel) {
                closeDrawer()
                openMenuDialog(slug, { room: true })
            } else {
                openQuickMenu(
                    id,
                    {
                        x: e.clientX,
                        y: e.clientY,
                    },
                    true,
                )
            }
        },
        [slug, isModel],
    )

    const onMessageClick = useCallback(() => {
        closeDrawer()
        setFullWindow(false)
        openMessage(
            slug,
            isSidebar ? TransitionTypes.SIDEBAR : TransitionTypes.MODAL,
        )
    }, [slug, isSidebar])

    if (viewerCount !== undefined) {
        return (
            <div css={subtitleStyles} className="typography gutter-bottom">
                {i18n.t('chat.participants')} ({viewerCount})
            </div>
        )
    }

    if (guestCount !== undefined) {
        const paidShow =
            streamType === 'club' ||
            streamType === 'group' ||
            streamType === 'private'

        if (paidShow) {
            return
        }

        const guestCountText = isSidebar
            ? `(${guestCount} Guests)`
            : `Guests: ${guestCount}`
        return (
            <div css={isSidebar ? guestsSidebarStyles : guestsStyles}>
                {guestCountText}
            </div>
        )
    }

    return (
        <div
            css={
                !isSidebar
                    ? participantContainerStyles
                    : participantContainerSidebarStyles
            }
        >
            <div
                css={!isSidebar ? participantStyles : participantSidebarStyles}
            >
                <div css={participantRowStyles} onClick={onClick}>
                    <BadgeAvatar
                        disableBadge={true}
                        offline={isOffline}
                        small={isSidebar}
                        size={avatarSize}
                        alt={name}
                        id={id}
                    />
                    <span
                        css={isSidebar ? nameSidebarStyles : nameStyles}
                        style={nameStyle}
                        data-testid="Container__ParticipantName"
                    >
                        {name}
                    </span>
                </div>
                {!isSidebar && (
                    <IconButton css={buttonStyles} onClick={onMessageClick}>
                        <Message />
                    </IconButton>
                )}
            </div>
        </div>
    )
})

const itemKeySelector = (item: ParticipantType) => {
    const { id, guestCount, viewerCount } = safeObj(item)
    if (id) return id
    if (guestCount !== undefined) return 'guestCount'
    if (viewerCount !== undefined) return 'viewerCount'
    return 'unknown'
}

interface IParticipants {
    id: number
    availableHeight?: number
    sidebar?: boolean
}

const Participants = memo((props: IParticipants) => {
    const vertical = useScreenStateSelector('portrait')
    const { id, availableHeight, sidebar = false } = props
    const leftNav = !vertical
    const streamType = useUserSelector(id, 'streamType')
    const roomParticipants = useRoomSelector(id, 'participants') ?? []
    const guestCount = useRoomSelector(id, 'guestCount') ?? 0
    const viewerCount = roomParticipants.length + guestCount
    const contentHeight = useScreenStateSelector('contentHeight')
    const resolvedHeight = sidebar ? contentHeight : availableHeight

    const list = useMemo(
        () =>
            roomParticipants.sort(
                (first, second) =>
                    second.level - first.level ||
                    (first.name?.toLowerCase() > second.name?.toLowerCase()
                        ? 1
                        : -1),
            ),
        [roomParticipants],
    )

    const participants = useMemo<Array<ParticipantType>>(
        () =>
            sidebar
                ? [{ viewerCount }, ...list, { guestCount }]
                : [...list, { guestCount }],
        [list, sidebar, guestCount, viewerCount],
    )

    const rowHeight = sidebar ? ROW_HEIGHT_SIDEBAR : ROW_HEIGHT
    const listLength = list.length + (sidebar ? 2 : 1)
    const listHeight = listLength * rowHeight
    const rootStylesDrawer = css({
        width: '100%',
        height: !leftNav
            ? `min(${listHeight}px, calc(max(250px, 70vh) - 48px))`
            : '100%',
    })
    const styles = sidebar ? rootStyles : rootStylesDrawer

    const rows = useMemo(
        () =>
            participants.map((item) => (
                <ParticipantRow
                    key={itemKeySelector(item)}
                    participant={item}
                    isSidebar={sidebar}
                    streamType={streamType}
                />
            )),
        [participants, sidebar, streamType],
    )

    return (
        <div css={styles} data-testid="Modal__Participants">
            {resolvedHeight && (
                <VList
                    className="no-scrollbars"
                    style={{ height: resolvedHeight, width: '100%' }}
                    itemSize={rowHeight}
                >
                    {rows}
                </VList>
            )}
        </div>
    )
})

export default Participants
